import { useState, useEffect, useCallback } from 'react';
import recipeAPI from '../services/api';
import { CATEGORIES, DIFFICULTIES, MAX_SCREENSHOTS } from '../constants/recipeConstants';

const RecipeForm = ({ recipeId, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    prep_time: '',
    servings: '',
    difficulty: 'Kolay',
    description: '',
    ingredients: [''],
    instructions: [''],
  });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [screenshots, setScreenshots] = useState([]); // Her item: { preview: url, file: File|null, id: number|null, isExisting: boolean }
  const [originalScreenshotIds, setOriginalScreenshotIds] = useState([]); // Orijinal yüklenen screenshot ID'leri

  // Collapse states
  const [showMainImage, setShowMainImage] = useState(false);
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const loadRecipe = useCallback(async () => {
  if (!recipeId) return;

  try {
    setLoading(true);
    const recipe = await recipeAPI.getRecipe(recipeId);

    setFormData({
      title: recipe.title,
      category: recipe.category,
      prep_time: recipe.prep_time,
      servings: recipe.servings,
      difficulty: recipe.difficulty,
      description: recipe.description,
      ingredients: recipe.ingredients.map(i => i.ingredient),
      instructions: recipe.instructions.map(i => i.instruction),
    });

    if (recipe.image) {
      setImagePreview(recipe.image);
    }

    if (recipe.screenshots && recipe.screenshots.length > 0) {
      const screenshotData = recipe.screenshots.map(s => ({
        preview: s.image_url,
        file: null,
        id: s.id,
        isExisting: true
      }));
      setScreenshots(screenshotData);
      setOriginalScreenshotIds(recipe.screenshots.map(s => s.id));
    }
  } catch (err) {
    setError('Tarif yüklenemedi: ' + err.message);
  } finally {
    setLoading(false);
  }
}, [recipeId]);

useEffect(() => {
  loadRecipe();
}, [loadRecipe]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleArrayChange = (index, field, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({
      ...formData,
      [field]: newArray,
    });
  };

  const addArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], ''],
    });
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [field]: newArray.length > 0 ? newArray : [''],
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScreenshotsChange = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Maksimum görsel kontrolü
    const remainingSlots = MAX_SCREENSHOTS - screenshots.length;
    if (remainingSlots <= 0) {
      alert(`En fazla ${MAX_SCREENSHOTS} adım görseli ekleyebilirsiniz.`);
      e.target.value = '';
      return;
    }

    // Kalan slot kadar dosya al
    const filesToAdd = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      alert(`Sadece ${remainingSlots} görsel eklenebilir. En fazla ${MAX_SCREENSHOTS} görsel yükleyebilirsiniz.`);
    }

    // Preview'ları oluştur
    const newScreenshots = [];
    let loadedCount = 0;

    filesToAdd.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newScreenshots[index] = {
          preview: reader.result,
          file: file,
          id: null,
          isExisting: false
        };
        loadedCount++;

        // Tüm dosyalar yüklendiğinde ekle
        if (loadedCount === filesToAdd.length) {
          setScreenshots(prev => [...prev, ...newScreenshots]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  };

  const removeScreenshot = (index) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        ingredients: formData.ingredients.filter(i => i.trim() !== ''),
        instructions: formData.instructions.filter(i => i.trim() !== ''),
        image: imageFile,
        screenshots: screenshots.filter(s => !s.isExisting).map(s => s.file),
      };

      // Düzenleme modunda silinecek screenshot ID'lerini gönder
      if (recipeId) {
        // Şu an kalan mevcut screenshot'ların ID'lerini al
        const currentExistingIds = screenshots
          .filter(s => s.isExisting)
          .map(s => s.id);

        // Silinmesi gereken ID'leri bul (orijinal - şu anki)
        const removedIds = originalScreenshotIds.filter(
          id => !currentExistingIds.includes(id)
        );

        // Silinecek ID'leri ekle
        if (removedIds.length > 0) {
          submitData.remove_screenshot_ids = removedIds;
        }

        await recipeAPI.updateRecipe(recipeId, submitData);
      } else {
        await recipeAPI.createRecipe(submitData);
      }

      onSuccess();
    } catch (err) {
      console.error('Form submission error:', err);
      // Detaylı hata mesajını göster
      const errorMessage = err.message || 'Bir hata oluştu';
      setError(errorMessage);

      // Sayfayı en üste kaydır ki kullanıcı hatayı görebilsin
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && recipeId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                {recipeId ? 'Tarif Düzenle' : 'Yeni Tarif Ekle'}
              </h1>
              <p className="text-purple-100 text-sm mt-1">
                <span className="text-red-300">*</span> Sadece tarif adı ve kategori zorunludur
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4 mb-6 animate-shake">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  {recipeId ? 'Güncelleme Hatası' : 'Oluşturma Hatası'}
                </h3>
                <div className="text-sm text-red-700 whitespace-pre-line">
                  {error}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setError('')}
                className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Temel Bilgiler - Zorunlu Alanlar */}
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Temel Bilgiler</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tarif Adı <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="örn: Mercimek Çorbası"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Seçiniz</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Ana Görsel - Collapsible */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMainImage(!showMainImage)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Ana Görsel (Opsiyonel)</h2>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${showMainImage ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showMainImage && (
              <div className="px-6 pb-6 space-y-4">
                {imagePreview && (
                  <div className="relative w-full h-48 rounded-xl overflow-hidden">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
              </div>
            )}
          </div>

          {/* Ek Bilgiler - Collapsible */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowExtraInfo(!showExtraInfo)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-500 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Ek Bilgiler (Opsiyonel)</h2>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${showExtraInfo ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showExtraInfo && (
              <div className="px-6 pb-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zorluk</label>
                    <select
                      name="difficulty"
                      value={formData.difficulty}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {DIFFICULTIES.map(diff => (
                        <option key={diff} value={diff}>{diff}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hazırlık Süresi</label>
                    <input
                      type="text"
                      name="prep_time"
                      value={formData.prep_time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="örn: 30 dakika"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Porsiyon</label>
                  <input
                    type="text"
                    name="servings"
                    value={formData.servings}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="örn: 4 kişilik"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Tarif hakkında kısa bir açıklama yazın..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Malzemeler - Collapsible */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowIngredients(!showIngredients)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Malzemeler (Opsiyonel)</h2>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${showIngredients ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showIngredients && (
              <div className="px-6 pb-6 mt-1">
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={() => addArrayItem('ingredients')}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all text-sm font-semibold"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.ingredients.map((ingredient, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={ingredient}
                        onChange={(e) => handleArrayChange(index, 'ingredients', e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={`Malzeme ${index + 1}`}
                      />
                      {formData.ingredients.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('ingredients', index)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Yapılış Adımları - Collapsible */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-2 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Yapılış Adımları (Opsiyonel)</h2>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform duration-200 ${showInstructions ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showInstructions && (
              <div className="px-6 pb-6">
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={() => addArrayItem('instructions')}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all text-sm font-semibold"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.instructions.map((instruction, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm mt-2">
                        {index + 1}
                      </div>
                      <textarea
                        value={instruction}
                        onChange={(e) => handleArrayChange(index, 'instructions', e.target.value)}
                        rows={2}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={`Adım ${index + 1}`}
                      />
                      {formData.instructions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeArrayItem('instructions', index)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all h-fit mt-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Adım Görselleri */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-lg font-bold text-gray-800">Adım Görselleri</label>
                <p className="text-xs text-gray-500 mt-1">En fazla {MAX_SCREENSHOTS} görsel ekleyebilirsiniz ({screenshots.length}/{MAX_SCREENSHOTS})</p>
              </div>
              <label className={`px-2 py-2 rounded-lg transition-all text-sm font-semibold ${screenshots.length >= MAX_SCREENSHOTS ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'}`}>
                + Görsel Ekle
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleScreenshotsChange}
                  disabled={screenshots.length >= MAX_SCREENSHOTS}
                  className="hidden"
                />
              </label>
            </div>
            <div className="space-y-4">
              {screenshots.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {screenshots.map((screenshot, index) => (
                    <div key={index} className="relative h-32 rounded-lg overflow-hidden group">
                      <img src={screenshot.preview} alt={`Screenshot ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeScreenshot(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-semibold">Görsel {index + 1}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">Henüz adım görseli eklenmedi</p>
                  <p className="text-gray-400 text-xs mt-1">Birden fazla görsel ekleyebilirsiniz</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 bg-gray-200 text-gray-800 py-4 rounded-xl font-semibold hover:bg-gray-300 transition-all"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Kaydediliyor...' : (recipeId ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeForm;
